<template>
  <div
    class="range-slider__range-container"
    :style="rangeContainerStyles"
  >
    <label
      class="range-slider__label visually-hidden"
      :for="id"
      v-text="label"
    />

    <input
      :id="id"
      v-model.number="localModelValue"
      class="range-slider__input"
      :class="{ [$class('active')]: active }"
      :disabled="disabled"
      :max="config.max"
      :min="config.min"
      :name="name"
      :step="config.step"
      type="range"
      @input="updateValue(localModelValue)"
      @mouseup="handleInteractionEnd"
      @touchend="handleInteractionEnd"
    >

    <div
      class="range-slider__controls"
      :class="`range-slider__controls--${type}`"
      :style="rangeControlsStyles"
    >
      <div class="range-slider__thumb" />

      <div class="range-slider__tooltip">
        <p
          class="range-slider__tooltip-text text-body-s-regular"
          aria-hidden="true"
        >
          <slot
            :is-max="isMax"
            :is-min="isMin"
            name="tooltipText"
            :type="type"
            :value="localModelValue"
          >
            <span v-text="tooltipText" />
          </slot>
        </p>
      </div>
    </div>

    <div
      class="range-slider__fill"
      :class="`range-slider__fill--${type}`"
      :style="rangeFillStyles"
    />
  </div>
</template>

<script>

/**
 * Vue: Range slider input (range-slider-input)
 * -----------------------------------------------------------------------------
 * Range slide range input and container.
 * - Uses classes and styles in range slider component.
 *
 * @param {Object} config - Configuration for slider.
 * @param {Boolean} [disabled] - Input is disabled.
 * @param {String} id - ID for input.
 * @param {String} label - Input label text.
 * @param {Number|Object} modelValue - Current value provided by v-model,
 * do not pass an object, only accepts object to avoid prop invalid errors.
 * @param {String} [name] - Input name attribute.
 * @param {String} rangeWidth - Input container width.
 * @param {String} type - Type of input, expects 'single', 'min', or 'max'.
 *
 * @emits interaction-end - When user finishes interacting with inputs.
 * @emits update-value - Debounced emit as the user updates the input value.
 * @emits update:model-value - Emit v-model update event.
 *
 */
export default {
  name: 'RangeSliderInput',

  props: {
    config: {
      type: Object,
      default: () => ({
        max: 100,
        min: 0,
        step: 1,
      }),
      required: true,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    id: {
      type: String,
      default: '',
      required: true,
    },
    label: {
      type: String,
      default: '',
      required: true,
    },
    modelValue: {
      type: [Number, Object],
      default: 0,
      required: true,
    },
    name: {
      type: String,
      default: '',
    },
    rangeWidth: {
      type: String,
      default: '100%',
    },
    type: {
      type: String,
      default: 'single',
      required: true,
    },
  },

  emits: {
    'interaction-end': (value) => typeof value === 'number',
    'update-value': (value) => typeof value === 'object',
    'update:model-value': (value) => typeof value === 'number',
  },

  data() {
    return {
      active: false,
      localModelValue: 0,
      timeout: {
        emit: '',
        interaction: '',
      },
    }
  },

  computed: {

    /**
     * Compute if max thumb is at max value.
     * @returns {Boolean}
     */
    isMax() {
      switch (this.type) {
        case 'single':
        case 'max':
          return this.localModelValue === this.config.max
        case 'min':
        default:
          return false
      }
    },

    /**
     * Compute if min thumb is at min value.
     * @returns {Boolean}
     */
    isMin() {
      switch (this.type) {
        case 'single':
        case 'min':
          return this.localModelValue === this.config.min
        case 'max':
        default:
          return false
      }
    },

    /**
     * Compute range container styles.
     * @returns {Object}
     */
    rangeContainerStyles() {
      return { width: this.rangeWidth }
    },

    /**
     * Compute range controls styles.
     * @returns {Object}
     */
    rangeControlsStyles() {
      const property = document.dir === 'rtl' ? 'right' : 'left'
      return { [property]: this.rangePosition }
    },

    /**
     * Compute range fill styles.
     * @returns {Object}
     */
    rangeFillStyles() {
      const property = document.dir === 'rtl' ? 'right' : 'left'

      if (this.type === 'single') {
        return {
          [property]: 0,
          width: this.rangePosition,
        }
      }

      return this.type === 'max'
        ? {
          [property]: 0,
          width: this.rangePosition,
        }
        : {
          [property]: this.rangePosition,
          width: `calc(100% - ${this.rangePosition} + 1px)`,
        }
    },

    /**
     * Compute range position.
     * @returns {Object|Number}
     */
    rangePosition() {
      const difference = this.config.max - this.config.min
      const decimal = (this.localModelValue - this.config.min) / difference

      /**
       * If decimal calculation does not return a number, falls back to setting
       * the range position to the min or max values.
       * - Handles cases where range position calculation fails, e.g. when the
       *  `difference` is 0.
       */
      if (isNaN(decimal)) {
        return this.type === 'min' ? '0%' : '100%'
      }

      return `${(decimal * 100).toFixed(2)}%`
    },

    /**
     * Compute default tooltip text.
     * - Used as fallback when slotted content isn't provided.
     * @returns {String}
     */
    tooltipText() {
      return `${this.localModelValue}%`
    },
  },

  watch: {

    /**
     * Watch v-model value to update local value.
     */
    modelValue() {
      this.localModelValue = this.modelValue
    },
  },

  mounted() {
    this.localModelValue = this.modelValue
  },

  methods: {

    /**
     * Handle interaction end with input.
     * - Adds is-active class to input for set interval so that tooltip is
     *   displayed if the user sets a value with a single touch/click.
     */
    handleInteractionEnd() {
      this.$emit('interaction-end', this.localModelValue)
      this.active = true

      /**
       * Reset after timeout.
       */
      if (this.timeout.interaction) {
        clearTimeout(this.timeout.interaction)
      }

      this.timeout.interaction = setTimeout(() => {
        this.active = false
      }, this.$timing('skeleton'))
    },

    /**
     * Emit update value update.
     * @param {Number} newValue - Updated value.
     */
    updateValue(newValue) {
      let sanitisedValue = newValue

      /**
       * Maximum values can ignore step amount.
       * - Correct max value so it aligns with a step value.
       */
      if (this.type === 'max' && newValue % this.config.step) {
        sanitisedValue = newValue - (newValue % this.config.step)
      }

      this.$emit('update:model-value', sanitisedValue)

      this.$emit(
        'update-value',
        {
          source: 'range',
          type: this.type,
          value: sanitisedValue,
        },
      )
    },
  },
}
</script>
